import { Markup, PointMarkup } from '@technology/web3d-plugin-markup';
import { Web3DViewer } from '@technology/web3d';
import {
  AngleFormatter,
  LengthFormatter,
  formatChainageLabel,
} from '@trimble/utilities';
import { Web3DViewerWrapper } from '@trimble/web3d-utils';
import { UnreachableCaseError } from 'ts-essentials';
import { LengthUnit } from '@fulcrum/lib';
import { AlignmentSettings, FormattingSettings } from '@trimble/types';
import { Measurement } from './measurement';
import { applyPointLabel } from './measurement-helpers';

const CUSTOM_LENGTH_UNIT = 'custom';

export interface MeasurementFormattingContext {
  items(): Measurement[];
  formattingSettings(): FormattingSettings;
  alignmentSettings(): AlignmentSettings | undefined;
  getViewerMarkups(viewer: Web3DViewer, measurement: Measurement): Markup[];
  getValidMarkups(markups: Markup[]): Markup[];
}

export function refreshMeasurementMarkupFormattersOnViewer(
  ctx: MeasurementFormattingContext,
  viewerWrapper: Web3DViewerWrapper
): void {
  const formattingSettings = ctx.formattingSettings();
  const alignmentSettings = ctx.alignmentSettings();

  for (const measurement of ctx.items()) {
    const markups = ctx.getViewerMarkups(viewerWrapper.viewer, measurement);
    for (const markup of markups) {
      switch (measurement.type) {
        case 'point':
          applyPointLabel(
            markup as PointMarkup,
            viewerWrapper.viewer,
            measurement.label,
            measurement.showXYCoordinates ?? true,
            measurement.showZCoordinate ?? true
          );
          break;
        case 'alignment':
          if (alignmentSettings) {
            applyChainageFormatterToMarkup(
              markup,
              alignmentSettings,
              formattingSettings
            );
          }
          break;
        case 'line':
        case 'angle':
        case 'polyline':
        case 'area':
        case 'volume':
        case 'passCount':
          break;
        default:
          throw new UnreachableCaseError(measurement);
      }
    }
  }
}

export function applyFormattingSettingsToViewers(
  ctx: MeasurementFormattingContext,
  formattingSettings: FormattingSettings,
  viewerWrappers: Web3DViewerWrapper[]
): void {
  for (const viewerWrapper of viewerWrappers) {
    // Point/alignment labels must have formatters before lengthUnit triggers
    // web3d's synchronous updateAllMeasurementText pass.
    refreshMeasurementMarkupFormattersOnViewer(ctx, viewerWrapper);

    viewerWrapper.viewer.settings.customLengthFormatter = (
      lengthInMeters: number
    ): string =>
      LengthFormatter.toDisplay({
        measuredValue: lengthInMeters,
        currentUnits: 'm',
        settings: formattingSettings,
        useMeasurementSettings: true,
      });

    viewerWrapper.viewer.settings.customAngleFormatter = (
      angleInRadians: number
    ): string =>
      AngleFormatter.toDisplay({
        measuredValue: angleInRadians,
        currentUnits: 'rad',
        settings: formattingSettings,
      });

    // Set lengthUnit last — web3d re-formats every measurement label when it changes.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    viewerWrapper.viewer.settings.lengthUnit = CUSTOM_LENGTH_UNIT as any;
  }
}

export function applyChainageFormatterToAlignmentMeasurements(
  ctx: MeasurementFormattingContext
): void {
  const settings = ctx.alignmentSettings();
  const fmtSettings = ctx.formattingSettings();

  for (const measurement of ctx.items()) {
    if (measurement.type !== 'alignment') continue;

    for (const markup of ctx.getValidMarkups(measurement.markups)) {
      if (settings) {
        applyChainageFormatterToMarkup(markup, settings, fmtSettings);
      } else {
        removeChainageFormatterFromMarkup(markup, fmtSettings);
      }
    }
  }
}

// web3d keeps the alignment distance label in a #private field and does not export
// its class; it is the only child label with an updateValue method.
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- internal web3d label; no public API for per-label formatting
function findAlignmentDistanceLabel(markup: Markup): any {
  return markup.children.find(
    (child) =>
      typeof (child as { updateValue?: unknown }).updateValue === 'function'
  );
}

export function applyChainageFormatterToMarkup(
  markup: Markup,
  settings: AlignmentSettings,
  fmtSettings: FormattingSettings
): void {
  const distanceLabel = findAlignmentDistanceLabel(markup);
  if (!distanceLabel) return;

  const displayUnit = (fmtSettings.lengthUnit ?? 'm') as LengthUnit;
  const decimals = fmtSettings.lengthDecimals;

  const chainageFormatter = (valueInMeters: number): string =>
    formatChainageLabel(
      valueInMeters,
      settings.labelFormat,
      displayUnit,
      decimals
    );

  distanceLabel._formatter = chainageFormatter;
  distanceLabel.text = chainageFormatter(distanceLabel._unformattedValue);
  distanceLabel.updateTexture();

  // Monkey-patch updateText (only once) so that when the web3d library
  // re-formats labels on settings changes, the chainage formatter is
  // re-applied instead of the generic length formatter.
  if (!distanceLabel._originalUpdateText) {
    distanceLabel._originalUpdateText =
      distanceLabel.updateText.bind(distanceLabel);
    distanceLabel.updateText = function (
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- internal options type from web3d
      options: any
    ): void {
      this._originalUpdateText(options);
      if (this._chainageFormatter) {
        this._formatter = this._chainageFormatter;
        this.text = this._chainageFormatter(this._unformattedValue);
      }
    };
  }
  distanceLabel._chainageFormatter = chainageFormatter;
}

export function removeChainageFormatterFromMarkup(
  markup: Markup,
  fmtSettings: FormattingSettings
): void {
  const distanceLabel = findAlignmentDistanceLabel(markup);
  if (!distanceLabel) return;

  distanceLabel._chainageFormatter = null;

  const standardFormatter = (valueInMeters: number): string =>
    LengthFormatter.toDisplay({
      measuredValue: valueInMeters,
      currentUnits: 'm',
      settings: fmtSettings,
      useMeasurementSettings: false,
    });

  distanceLabel._formatter = standardFormatter;
  distanceLabel.text = standardFormatter(distanceLabel._unformattedValue);
  distanceLabel.updateTexture();
}
